import { useCallback, useLayoutEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import ThemeBackground from '../theme/ThemeBackground';
import { adoptProfile, clearAppearance } from '../theme/appearance';
import { useAuth } from './AuthContext';
import AppHeader from './AppHeader';
import { AccountSheet, PageTabs, TabBar, splitMenu, tabSplit, useBelt } from './AppNav';

/*
  THE SHARED FRAME for every signed-in page. Theme system, phase 3, 14 September
  2026; the one structural change in docs/THEME-SYSTEM.md's rollout.

  **Before this, every screen built its own frame**: the four dashboards, the
  system states and count entry each wrote out the same desk tree
  (.app-shell.dash > .main-col > the bar > .content-area) and the same phone
  tree (.oh-phone-shell > header > a scrolling body > dock and sheet), by hand,
  so a background would have had to be added a dozen times and would have
  drifted. Now:

  - `AppShell` wraps every signed-in route in src/App.jsx and draws the theme's
    still background once, behind all of them.
  - `DeskFrame` and `PhoneFrame` are the two trees, written once. Each screen
    still decides what goes IN them (its own bar with its own tabs, its own
    header, dock and sheets), because those carry the screen's own state; the
    frame around them is no longer the screen's business.

  **What they draw was exactly what the screens drew**, element for element and
  class for class. Since rebrand step 4 they also draw the navigation, below.

  Not on this frame, deliberately:
  - The sign-in, password and waiting screens: nobody is known there, so there
    is no theme to show (docs/THEME-SYSTEM.md section 6, theme phase 3b).
  - Count entry below 1024, whose floating header and count bar are a frame of
    their own.
  - A submitted or approved count sheet (LockedView), which draws the same top
    bar markup itself (its rail went in rebrand step 4).
  Both still sit on the shared background, because AppShell wraps their route.
*/
export function AppShell() {
  /*
    **The person's theme, applied before the first paint** (theme phase 4, 14
    September 2026). This frame only mounts once the profile is known, so the
    first signed-in screen is drawn already in the person's theme, never in the
    default and then swapped. Leaving the frame (signing out) takes the theme
    off <html>, so the sign-in screen, which reads no theme, never wears the
    last person's. See src/theme/appearance.js.
  */
  const { profile } = useAuth();
  useLayoutEffect(() => { adoptProfile(profile); }, [profile]);
  useLayoutEffect(() => () => clearAppearance(), []);

  return (
    <>
      <ThemeBackground />
      <Outlet />
    </>
  );
}

/*
  THE NAVIGATION, rebrand step 4, 17 September 2026. A screen hands its frame
  `nav`, which is what it already knew: its areas from the one menu, the page it
  is on, how it changes page, and who is signed in. The frame draws the top bar,
  the page pills, the belt slide and, on a phone, the tab bar and the account
  sheet, so the four dashboards and the stopped screens draw them identically.

    nav = { areas, activeKey, onSelect, userName, email, roleLabel, onSignOut }

  `header` is still accepted in place of `nav`, for count entry, which has a bar
  of its own with no areas in it.
*/
function useNavParts(nav) {
  const areas = (nav && nav.areas) || [];
  const activeKey = nav ? nav.activeKey : null;
  const order = useCallback(key => {
    const i = areas.flatMap(a => a.pages).findIndex(p => p.key === key);
    return i < 0 ? 0 : i;
  }, [areas]);
  const belt = useBelt(activeKey, order, nav ? nav.onSelect : null);
  const area = areas.find(a => a.pages.some(p => p.key === activeKey)) || null;
  return { areas, activeKey, belt, area };
}

/* The desk tree: the bar above, the page's content in the scrolling area, and
   `after` for what a screen keeps beside the column (count entry's dialogs). */
export function DeskFrame({ header, nav, fill = false, contentClassName, contentStyle, after, children }) {
  const { areas, activeKey, belt, area } = useNavParts(nav);
  /* `area` goes up to the bar now rather than being drawn here. The pages are
     tier 2 inside the bar again, the owner's decision of 3 October 2026. */
  const bar = nav ? (
    <AppHeader areas={areas} activeKey={activeKey} onSelect={belt.go} area={area}
      userName={nav.userName} roleLabel={nav.roleLabel} onSignOut={nav.onSignOut} />
  ) : header;
  return (
    <div className="app-shell dash">
      <div className="main-col">
        {bar}
        <div className={contentClassName ? 'content-area ' + contentClassName : 'content-area'} style={contentStyle}>
          {nav ? (
            <div className={'oh-page-col' + (fill ? ' is-fill' : '')}>
              <div ref={belt.ref} className="oh-page">{children}</div>
            </div>
          ) : children}
        </div>
      </div>
      {after}
    </div>
  );
}

/* The held tree, everything under 1024: the floating bar, a scrolling body clear
   of it and of the tab bar, then the tab bar and the account sheet. */
export function PhoneFrame({ header, nav, fill = false, bodyStyle, footer, children }) {
  const { areas, activeKey, belt, area } = useNavParts(nav);
  const [accountOpen, setAccountOpen] = useState(false);
  const { main, settings } = splitMenu(areas);
  return (
    <div className="oh-phone-shell">
      {nav ? (
        <AppHeader held areas={areas} activeKey={activeKey} onSelect={belt.go}
          userName={nav.userName} roleLabel={nav.roleLabel} onSignOut={nav.onSignOut}
          onAccount={() => setAccountOpen(true)} accountOpen={accountOpen} />
      ) : header}
      <div className={'oh-held-body' + (nav ? ' has-tabbar' : '') + (fill ? ' is-fill' : '')} style={{ flex: 1, ...bodyStyle }}>
        {nav && <PageTabs area={area} activeKey={activeKey} onSelect={belt.go} />}
        <div ref={belt.ref} className="oh-page">{children}</div>
      </div>
      {nav && (
        <>
          <TabBar areas={main} activeKey={activeKey} onSelect={belt.go} />
          <AccountSheet
            open={accountOpen}
            onClose={() => setAccountOpen(false)}
            settings={settings}
            overflow={tabSplit(main).overflow}
            activeKey={activeKey}
            onSelect={belt.go}
            name={nav.userName}
            email={nav.email}
            roleLabel={nav.roleLabel}
            onSignOut={nav.onSignOut}
          />
        </>
      )}
      {footer}
    </div>
  );
}
